import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import QRCode from "qrcode";
import useAuthStatus from "../../hooks/useAuthStatus";
import MobileBottomNav from "../../components/feature/MobileBottomNav";
import { getUserWallet } from "../../api/apiServices";

export default function DepositCrypto() {
  const navigate = useNavigate();
  const isAuthenticated = useAuthStatus();

  const [selectedSymbol, setSelectedSymbol] = useState("USDT");
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [walletData, setWalletData] = useState(null);
  const [qrCodeUrl, setQrCodeUrl] = useState("");

  useEffect(() => {
    if (!isAuthenticated) return;
    const fetchWallets = async () => {
      try {
        setLoading(true);
        const res = await getUserWallet();
        if (res.status === true) {
          setWalletData(res.data);
        } else {
          setError(res.message || "Failed to load wallets");
        }
      } catch (err) {
        console.error("Error loading wallet:", err);
        setError("Failed to load wallet details");
      } finally {
        setLoading(false);
      }
    };
    fetchWallets();
  }, [isAuthenticated]);

  const wallets = walletData?.wallets || [];
  const currentWallet = wallets.find(w => w.currency === selectedSymbol) || wallets[0];

  useEffect(() => {
    if (!currentWallet?.address) return;
    QRCode.toDataURL(currentWallet.address, {
      width: 280,
      margin: 2,
      color: { dark: "#0F4C35", light: "#FFFFFF" },
    })
      .then(setQrCodeUrl)
      .catch(err => console.error("QR Code generation failed:", err));
  }, [selectedSymbol, walletData]);

  const handleCopy = () => {
    if (!currentWallet?.address) return;
    navigator.clipboard.writeText(currentWallet.address);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#F7F8FA] flex flex-col items-center justify-center px-5 text-center pb-28">
        <i className="ri-lock-2-line text-6xl text-slate-300 mb-6" />
        <h2 className="text-2xl font-bold text-slate-800 mb-2">Sign in Required</h2>
        <p className="text-slate-600 mb-8">You need to be logged in to deposit crypto</p>
        <button onClick={() => navigate("/auth")} className="px-8 py-3 bg-emerald-600 text-white rounded-2xl font-semibold">
          Sign In
        </button>
        <MobileBottomNav />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F8FA] pb-28" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
      {/* Header — back goes to /deposit chooser */}
      <div className="bg-white px-5 pt-12 pb-4 flex items-center gap-3 border-b border-slate-100">
        <button
          onClick={() => navigate("/deposit")}
          className="w-9 h-9 rounded-xl bg-slate-100 flex items-center justify-center hover:bg-slate-200"
        >
          <i className="ri-arrow-left-line text-slate-600 text-lg" />
        </button>
        <div>
          <h1 className="text-slate-800 font-bold text-lg">Deposit Crypto</h1>
          <p className="text-slate-400 text-xs">Send to your wallet address</p>
        </div>
      </div>

      <div className="px-5 py-5 space-y-5">
        <p className="text-slate-500 text-sm">Select the asset you want to deposit</p>

        {/* Asset selector */}
        <div className="flex gap-2 overflow-x-auto pb-1">
          {wallets.map((wallet) => (
            <button
              key={wallet.currency}
              onClick={() => setSelectedSymbol(wallet.currency)}
              className="flex-shrink-0 px-4 py-2 rounded-xl border transition-all whitespace-nowrap"
              style={{
                background: selectedSymbol === wallet.currency ? "#10B98115" : "#fff",
                borderColor: selectedSymbol === wallet.currency ? "#10B981" : "#E2E8F0",
                color: selectedSymbol === wallet.currency ? "#10B981" : "#94A3B8",
              }}
            >
              <span className="text-xs font-bold">{wallet.currency}</span>
            </button>
          ))}
        </div>

        {loading ? (
          <div className="bg-white rounded-2xl p-12 flex flex-col items-center border border-slate-100">
            <div className="w-8 h-8 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin mb-4" />
            <p className="text-slate-400 text-sm">Loading wallet…</p>
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-center">
            <p className="text-red-600 text-sm">{error}</p>
            <button onClick={() => window.location.reload()} className="mt-4 text-emerald-600 text-sm font-medium">
              Retry
            </button>
          </div>
        ) : currentWallet ? (
          <div className="bg-white border border-slate-100 rounded-2xl p-5 space-y-5">
            {/* Wallet header */}
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-800 font-bold text-sm">{currentWallet.currency} Deposit Address</p>
                <p className="text-slate-400 text-xs mt-0.5">
                  Network: <span className="font-semibold text-slate-600">{currentWallet.network || "BSC (BEP20)"}</span>
                </p>
              </div>
              <div className="w-11 h-11 rounded-full bg-emerald-100 flex items-center justify-center">
                <span className="font-bold text-emerald-600 text-sm">{currentWallet.currency}</span>
              </div>
            </div>

            {/* QR code */}
            <div className="bg-slate-50 border border-slate-100 rounded-2xl p-6 flex flex-col items-center justify-center">
              {qrCodeUrl ? (
                <>
                  <img src={qrCodeUrl} alt="Wallet QR Code" className="w-52 h-52 rounded-xl" />
                  <p className="text-slate-400 text-xs mt-3">Scan to get the address</p>
                </>
              ) : (
                <div className="w-52 h-52 bg-white rounded-xl flex flex-col items-center justify-center gap-2">
                  <div className="w-5 h-5 border-2 border-emerald-400 border-t-transparent rounded-full animate-spin" />
                  <p className="text-slate-400 text-xs">Generating QR…</p>
                </div>
              )}
            </div>

            {/* Address row */}
            <div className="bg-slate-50 border border-slate-100 rounded-xl p-4 flex items-center gap-3">
              <p className="flex-1 text-slate-700 text-xs break-all font-mono leading-relaxed">
                {currentWallet.address}
              </p>
              <button
                onClick={handleCopy}
                className="w-10 h-10 rounded-xl flex items-center justify-center transition-all flex-shrink-0"
                style={{ background: copied ? "#059669" : "#E2E8F0" }}
              >
                <i
                  className={`${copied ? "ri-check-line" : "ri-file-copy-line"} text-lg`}
                  style={{ color: copied ? "#fff" : "#64748B" }}
                />
              </button>
            </div>

            {copied && (
              <p className="text-emerald-600 text-center text-sm font-medium">✅ Address copied!</p>
            )}
          </div>
        ) : null}

        {/* Warning */}
        {currentWallet && !loading && (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl px-4 py-4 flex gap-3">
            <i className="ri-alert-line text-amber-500 text-xl mt-0.5 flex-shrink-0" />
            <p className="text-xs text-amber-700 leading-relaxed">
              Only send <strong>{currentWallet.currency}</strong> on the{" "}
              <strong>{currentWallet.network || "BSC"}</strong> network to this address.
              Sending any other asset or using the wrong network will result in{" "}
              <strong>permanent loss of funds.</strong>
            </p>
          </div>
        )}
      </div>

      <MobileBottomNav />
    </div>
  );
}
